import React, { useState, useCallback, useEffect } from 'react';
import { BaseChart } from '@components/common';
import { chartOption } from '@components/metrics-panel-hz/utils/chartSchema';
import { useSelector } from 'react-redux';
import { filterColorSelector } from '@store/common/selectors';
import { formatSeries } from '@components/metrics-panel-hz/hooks/useMetricSeries';
import { formatTimeseriesData } from '@components/metrics-panel-hz/utils/format';
import { MetricName, LoadingText, LoadingContainer } from '../styled';
import MarkdownComponent from './Markdown';
import Loading from '@icons/prompt/prompt-summary-loading.svg';

function MetricCard({ metricName, data, allMetrics }) {
  const filterColor = useSelector(filterColorSelector);
  const [instance, setInstance] = useState(null);
  const chartInstanceRefCb = useCallback(
    (instance) => {
      setInstance(instance);
      instance.setOption(chartOption({ metric: metricName, allMetrics }));
    },
    [allMetrics, metricName],
  );

  useEffect(() => {
    if (instance && data?.length) {
      const option = instance.getOption();
      if (!option) {
        return;
      }
      const { series, xAxis, yAxis, tooltip } = formatSeries({
        currentMetricsMeta: allMetrics,
        data,
        metric: metricName,
        option,
        params: { filterColor },
        barChart: false,
        logAxis: false,
      });
      instance.setOption({ series, xAxis, yAxis, tooltip });
    }
  }, [instance, data, metricName, filterColor, allMetrics]);

  return (
    <div>
      <MetricName>{allMetrics[metricName].displayName || metricName}</MetricName>
      <BaseChart
        key="prompt-ai-chart"
        chartId="prompt-ai-chart"
        chartInstanceRefCb={chartInstanceRefCb}
        style={{ height: '400px', minHeight: 240, width: '750px' }}
      />
    </div>
  );
}

function MetricCardList({ metricQueryInfo, explanation, allMetricsObj, allMetricMeta, timestamp }) {
  if (!metricQueryInfo) {
    return null;
  } else if (timestamp && metricQueryInfo && metricQueryInfo.status !== 'done') {
    // cancel scenario
    return null;
  } else if (metricQueryInfo && metricQueryInfo.status !== 'done') {
    return (
      <LoadingContainer>
        <Loading />
        <LoadingText>Analyzing...</LoadingText>
      </LoadingContainer>
    );
  }
  // here we should check the metric is supported or not
  // we must be sure every metric is valid in frontend
  const allMetricValid = (metricQueryInfo.metricList || []).every(
    (metricName) => !!allMetricsObj[metricName],
  );
  if (!allMetricValid) {
    return null;
  }

  // check where the metric comes from
  // if comes from APP use app look up logic
  // if in EI page the original logic will format the data according the product type
  // actually use should use logic according to where the metrics belong
  const useAppLookup = !!allMetricMeta.app[metricQueryInfo.metricList[0]];

  const format = formatTimeseriesData({
    metrics: metricQueryInfo.metricList,
    data: metricQueryInfo.data.data,
    interval: {
      from: metricQueryInfo.interval[0],
      to: metricQueryInfo.interval[1],
      granularity: metricQueryInfo.granularity,
    },
    useAppLookupForPrompt: useAppLookup,
    annotations: [],
  });

  return (
    <div>
      {metricQueryInfo.metricList.map((metricName) => {
        return (
          <div key={metricName}>
            <MetricCard
              key={metricName}
              metricName={metricName}
              data={format[metricName]}
              allMetrics={allMetricsObj}
            />
            {explanation[metricName] ? (
              <MarkdownComponent text={explanation[metricName].text} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default MetricCardList;
